import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import productService from '../../../services/productService';
import AppDataGrid from '../../common/AppDataGrid';
import GridActionsCell from '../../common/GridActionsCell';
import TableSearch from '../../common/TableSearch';
import ActiveFilter from '../../common/ActiveFilter';
import { useDataTable } from '../../../hooks/useDataTable';
import { buildToggleAction, useCanUpdate } from '../../common/activeToggle';

const AttributesList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const canToggle = useCanUpdate('attributes.update');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [attributeToDelete, setAttributeToDelete] = useState(null);

  // Page, size, search, sort and the active filter live in the URL (/attributes?page=0&size=20&...).
  const table = useDataTable(productService.getAttributesPage, {}, {
    onError: () => toast.error(t('attributes.fetchError')),
  });
  const fetchAttributes = table.refresh;

  const handleDeleteClick = (attribute) => {
    setAttributeToDelete(attribute);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await productService.deleteAttribute(attributeToDelete.id);
      toast.success(t('attributes.deleteSuccess'));
      setDeleteDialogOpen(false);
      setAttributeToDelete(null);
      fetchAttributes();
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.deleteError'));
      console.error(error);
    }
  };

  const handleToggleActive = async (row) => {
    try {
      await productService.toggleAttributeActive(row.id);
      toast.success(row.isActive === false ? t('common.activated') : t('common.deactivated'));
      fetchAttributes();
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.toggleError'));
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setAttributeToDelete(null);
  };

  const columns = [
    // { field: 'id', headerName: t('common.id'), width: 70 },
    { field: 'name', headerName: t('common.name'), width: 200 },
    { field: 'type', headerName: t('common.type'), width: 120 },
    {
      field: 'unit',
      headerName: t('attributes.attribute'),
      width: 100,
      cellRenderer: (params) => params.value || 'N/A',
    },
    {
      field: 'isRequired',
      headerName: t('common.yes'),
      width: 100,
      cellRenderer: (params) => (
        <Chip
          label={params.value ? t('common.yes') : t('common.no')}
          color={params.value ? 'error' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'valueCount',
      headerName: t('attributes.values'),
      width: 110,
      cellRenderer: (params) => (
        <Chip label={params.value || 0} size="small" color="primary" />
      ),
    },
    { field: 'sortOrder', headerName: t('common.type'), width: 100 },
    {
      field: 'isActive',
      headerName: t('common.status'),
      width: 110,
      cellRenderer: (params) => (
        <Chip
          label={params.value === false ? t('common.inactive') : t('common.active')}
          color={params.value === false ? 'default' : 'success'}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: t('common.actions'),
      width: 150,
      sortable: false,
      cellRenderer: (params) => (
        <GridActionsCell
          actions={[
            {
              key: 'view',
              icon: VisibilityIcon,
              title: t('common.details'),
              color: 'info',
              onClick: () => navigate(`/attributes/view/${params.data.id}`),
            },
            {
              key: 'edit',
              icon: EditIcon,
              title: t('common.edit'),
              color: 'primary',
              onClick: () => navigate(`/attributes/edit/${params.data.id}`),
            },
            buildToggleAction({ row: params.data, onToggle: handleToggleActive, t, canToggle }),
            {
              key: 'delete',
              icon: DeleteIcon,
              title: t('common.delete'),
              color: 'error',
              onClick: () => handleDeleteClick(params.data),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">{t('attributes.title')}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/attributes/create')}
        >
          {t('attributes.createAttribute')}
        </Button>
      </Box>

      <Card>
        <CardContent>
          <TableSearch
            placeholder={t('common.search')}
            value={table.searchInput}
            onChange={table.handleSearchChange}
            sx={{ mb: 2 }}
          />
          <ActiveFilter
            value={table.params.active || 'all'}
            onChange={(active) => table.updateParams({ active })}
            sx={{ mb: 2 }}
          />
          <AppDataGrid
            rows={table.data.content}
            columns={columns}
            loading={table.loading}
            paginationMode="server"
            rowCount={table.data.totalElements}
            paginationModel={table.paginationModel}
            onPaginationModelChange={table.handlePaginationModelChange}
            sortModel={table.sortModel}
            onSortModelChange={table.handleSortChange}
            pageSizeOptions={[10, 20, 50, 100]}
            getRowId={(row) => row.id}
          />
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>{t('attributes.deleteAttribute')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('common.deleteConfirmText', { name: attributeToDelete?.name })}
            {attributeToDelete?.valueCount > 0 && (
              <>
                <br />
                <strong style={{ color: 'red' }}>
                  {t('attributes.values')}: {attributeToDelete.valueCount}
                </strong>
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AttributesList;
